import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus, Loader2, Pencil, Trash2, Play, Square, ClipboardList, Search, Archive,
} from 'lucide-react';
import { TaskDocsPopover } from '../components/client/TaskDocsPopover';
import { ArchivedTasks } from '../components/tasks/ArchivedTasks';
import { Sidebar, Topbar, CategoryChip, TASK_CATEGORY_ORDER, NewTaskDialog } from './Dashboard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { auth, tasks, clients, team, getStoredUser, clientService } from '../api';

export default function Tasks() {
  const nav = useNavigate();
  const { toast } = useToast();
  const [user, setUser] = useState(getStoredUser());
  const [loading, setLoading] = useState(true);
  const [taskList, setTaskList] = useState([]);
  const [clientList, setClientList] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [activeTimer, setActiveTimer] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('Open');
  const [query, setQuery] = useState('');
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [docStatus, setDocStatus] = useState({});
  const [editingTask, setEditingTask] = useState(null);
  const [archivedList, setArchivedList] = useState(null);
  const loadArchived = () => tasks.archived().then(setArchivedList).catch(() => setArchivedList([]));
  useEffect(() => { if (statusFilter === 'Archived') loadArchived(); }, [statusFilter]);
  const refreshDocStatus = () => clientService.docStatus().then(setDocStatus).catch(() => {});
  useEffect(() => { refreshDocStatus(); }, []);
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get('highlight');

  useEffect(() => {
    if (!highlightId || loading) return;
    setStatusFilter('All');
    setCategoryFilter('All');
    setQuery('');
    const t = setTimeout(() => document.getElementById(`task-row-${highlightId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 150);
    return () => clearTimeout(t);
  }, [highlightId, loading]);

  const fetchAll = async () => {
    try {
      const [me, tl, cl, at, tm] = await Promise.all([
        auth.me().catch(() => null),
        tasks.list(),
        clients.list(),
        tasks.activeTimer().catch(() => ({ active: null })),
        team.list().catch(() => ({ members: [] })),
      ]);
      if (me) setUser(me);
      setTaskList(tl);
      setClientList(cl);
      setTeamMembers(tm.members || []);
      if (at?.active) {
        setActiveTimer({ taskId: at.active.id, startedAt: at.active.timerStartedAt });
      } else {
        setActiveTimer(null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem('ad_token')) { nav('/login'); return; }
    fetchAll();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!activeTimer) return;
    const iv = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [activeTimer]);

  const startTimer = async (task) => {
    try {
      const updated = await tasks.startTimer(task.id);
      setActiveTimer({ taskId: updated.id, startedAt: updated.timerStartedAt });
      toast({ title: 'Timer started', description: updated.title });
      fetchAll();
    } catch (err) {
      toast({ title: 'Could not start timer', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    }
  };

  const stopTimer = async (id) => {
    try {
      const updated = await tasks.stopTimer(id);
      setActiveTimer(null);
      toast({ title: 'Timer stopped', description: `${(updated.hours || 0).toFixed(2)}h logged on "${updated.title}"` });
      fetchAll();
    } catch (err) {
      toast({ title: 'Could not stop timer', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    }
  };

  const changeStatus = async (id, status) => {
    try {
      const orig = taskList.find((t) => t.id === id);
      if (!orig) return;
      const payload = {
        title: orig.title, clientId: orig.clientId, clientName: orig.clientName,
        assigneeId: orig.assigneeId, status, dueDate: orig.dueDate,
        hours: orig.hours || 0, category: orig.category,
      };
      const updated = await tasks.update(id, payload);
      setTaskList((prev) => prev.map((t) => (t.id === id ? updated : t)));
      toast({ title: 'Task updated', description: `"${orig.title}" → ${status}` });
      fetchAll();
    } catch (err) {
      toast({ title: 'Update failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    }
  };

  const removeTask = async (t) => {
    if (!window.confirm(`Delete "${t.title}"?`)) return;
    try {
      await tasks.remove(t.id);
      toast({ title: 'Task deleted' });
      fetchAll();
    } catch (err) {
      toast({ title: 'Delete failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    }
  };

  const fail = (title) => (err) => toast({ title, description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
  const archiveDone = () => {
    const n = taskList.filter((t) => t.status === 'Done').length;
    if (!window.confirm(`Archive all ${n} done task(s)? You can restore them from the Archived tab.`)) return;
    tasks.archiveDone().then((r) => { toast({ title: 'Done tasks archived', description: `${r.archived} task(s) moved to Archived.` }); fetchAll(); }).catch(fail('Archive failed'));
  };
  const restoreTask = (t) => tasks.restore(t.id).then(() => { toast({ title: 'Task restored', description: `"${t.title}" is back in Done.` }); loadArchived(); fetchAll(); }).catch(fail('Restore failed'));
  const deleteArchived = (t) => window.confirm(`Permanently delete "${t.title}"? This cannot be undone.`)
    && tasks.remove(t.id).then(() => { toast({ title: 'Task deleted permanently' }); loadArchived(); }).catch(fail('Delete failed'));
  const purgeArchived = () => window.confirm(`Permanently delete all ${archivedList.length} archived task(s)? This cannot be undone.`)
    && tasks.purgeArchived().then((r) => { toast({ title: 'Archive emptied', description: `${r.deleted} task(s) deleted.` }); loadArchived(); }).catch(fail('Delete failed'));

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 grid place-items-center">
        <div className="flex items-center gap-2 text-stone-600"><Loader2 className="animate-spin" size={18}/> Loading tasks...</div>
      </div>
    );
  }

  // Filtering
  const visibleByStatus = taskList.filter((t) => {
    if (statusFilter === 'Open') return t.status !== 'Done';
    if (statusFilter === 'Done') return t.status === 'Done';
    return true;
  });
  const visibleByCat = visibleByStatus.filter((t) => categoryFilter === 'All' || (t.category || 'Other') === categoryFilter);
  const visible = visibleByCat.filter((t) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (t.title || '').toLowerCase().includes(q)
        || (t.clientName || '').toLowerCase().includes(q)
        || (t.assignee || '').toLowerCase().includes(q);
  });

  // Counts for category tabs (across current status filter)
  const counts = { All: visibleByStatus.length };
  visibleByStatus.forEach((t) => {
    const c = t.category || 'Other';
    counts[c] = (counts[c] || 0) + 1;
  });

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <Sidebar activeLabel="Tasks"/>
      <div className="flex-1 min-w-0">
        <Topbar user={user} firm={user?.firm}/>
        <main className="p-4 lg:p-8 space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-serif font-semibold text-stone-900 flex items-center gap-2">
                <ClipboardList size={24} className="text-emerald-800"/> Tasks
              </h1>
              <p className="mt-1 text-stone-600 text-sm">Every SARS, CIPC, VAT, PAYE and manual task across your firm.</p>
            </div>
            <div className="flex gap-2">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
                <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search title, client, assignee" className="pl-9 h-10 w-72"/>
              </div>
              <Button onClick={() => setTaskDialogOpen(true)} className="bg-emerald-800 hover:bg-emerald-900 text-white h-10">
                <Plus size={16} className="mr-2"/> New task
              </Button>
            </div>
          </div>

          {/* Status tabs */}
          <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-lg border border-stone-200 bg-white p-1">
            {['Open', 'Done', 'All', 'Archived'].map((s) => (
              <button
                key={s}
                type="button"
                data-testid={`task-status-tab-${s.toLowerCase()}`}
                onClick={() => setStatusFilter(s)}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${statusFilter === s ? 'bg-emerald-800 text-white' : 'text-stone-600 hover:text-stone-900'}`}
              >
                {s}
                <span className={`ml-2 text-xs ${statusFilter === s ? 'text-emerald-100' : 'text-stone-400'}`}>
                  {s === 'Open' ? taskList.filter((t) => t.status !== 'Done').length
                   : s === 'Done' ? taskList.filter((t) => t.status === 'Done').length
                   : s === 'Archived' ? (archivedList ? archivedList.length : '')
                   : taskList.length}
                </span>
              </button>
            ))}
          </div>
          {statusFilter === 'Done' && taskList.some((t) => t.status === 'Done') && (
            <Button variant="outline" onClick={archiveDone} className="h-10" data-testid="archive-done-btn"><Archive size={15} className="mr-2"/> Archive done tasks</Button>
          )}
          </div>

          {statusFilter === 'Archived' ? (
            archivedList ? <ArchivedTasks list={archivedList} onRestore={restoreTask} onDelete={deleteArchived} onPurge={purgeArchived}/>
              : <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-stone-400"/></div>
          ) : (

          <div className="rounded-xl bg-white border border-stone-200 overflow-hidden">
            <div className="flex flex-col gap-3 p-5 border-b border-stone-100">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="font-semibold text-stone-900">Filter by category</div>
                  <div className="text-xs text-stone-500 mt-0.5">Tap a category to focus on that stream of work.</div>
                </div>
                <Badge variant="outline" className="border-stone-300">{visible.length} showing</Badge>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {TASK_CATEGORY_ORDER.filter((c) => c === 'All' || counts[c]).map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCategoryFilter(c)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${categoryFilter === c ? 'bg-emerald-800 text-white border-emerald-800' : 'bg-white text-stone-700 border-stone-200 hover:border-emerald-500'}`}
                  >
                    {c} <span className={`ml-1 ${categoryFilter === c ? 'text-emerald-100' : 'text-stone-400'}`}>{counts[c] || 0}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-5 py-3 font-medium">Task</th>
                    <th className="text-left px-5 py-3 font-medium">Category</th>
                    <th className="text-left px-5 py-3 font-medium">Client</th>
                    <th className="text-left px-5 py-3 font-medium">Assignee</th>
                    <th className="text-left px-5 py-3 font-medium">Due</th>
                    <th className="text-right px-5 py-3 font-medium">Hours</th>
                    <th className="text-left px-5 py-3 font-medium">Status</th>
                    <th className="text-center px-5 py-3 font-medium">Timer</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {visible.length === 0 && (
                    <tr><td colSpan={9} className="px-5 py-10 text-center text-stone-500">
                      {taskList.length === 0
                        ? <>No tasks yet. Click <strong>New task</strong> to add your first one.</>
                        : <>No tasks match this filter.</>}
                    </td></tr>
                  )}
                  {visible.map((t) => {
                    const isRunning = activeTimer?.taskId === t.id;
                    const dueLabel = t.dueDate ? new Date(t.dueDate).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
                    const dueDate = t.dueDate ? new Date(t.dueDate) : null;
                    const overdue = dueDate && t.status !== 'Done' && dueDate < new Date();
                    return (
                      <tr key={t.id} id={`task-row-${t.id}`} data-testid={`task-row-${t.id}`} className={`transition-colors ${t.id === highlightId ? 'bg-amber-50 ring-2 ring-inset ring-amber-300' : isRunning ? 'bg-emerald-50/50' : 'hover:bg-stone-50'}`}>
                        <td className="px-5 py-4 font-medium text-stone-900">
                          <div className="flex items-center gap-2">
                            {isRunning && <span className="h-2 w-2 rounded-full bg-emerald-600 animate-pulse"/>}
                            {t.title}
                          </div>
                        </td>
                        <td className="px-5 py-4"><CategoryChip category={t.category || 'Other'}/></td>
                        <td className="px-5 py-4 text-stone-700">
                          {t.clientName || '—'}
                          {t.status !== 'Done' && t.clientId && docStatus[t.clientId] && (
                            <div><TaskDocsPopover task={t} status={docStatus[t.clientId]} onChanged={refreshDocStatus}/></div>
                          )}
                        </td>
                        <td className="px-5 py-4 text-stone-700">{t.assignee}</td>
                        <td className={`px-5 py-4 ${overdue ? 'text-red-700 font-medium' : 'text-stone-700'}`}>{dueLabel}</td>
                        <td className="px-5 py-4 text-right tabular-nums text-stone-700">{(t.hours || 0).toFixed(2)}h</td>
                        <td className="px-5 py-4">
                          <Select value={t.status} onValueChange={(v) => changeStatus(t.id, v)}>
                            <SelectTrigger className="h-8 w-[140px] text-xs"><SelectValue/></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="To Do">To Do</SelectItem>
                              <SelectItem value="In Progress">In Progress</SelectItem>
                              <SelectItem value="Review">Review</SelectItem>
                              <SelectItem value="Done">Done</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-5 py-4 text-center">
                          {isRunning ? (
                            <button onClick={() => stopTimer(t.id)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-red-50 text-red-700 hover:bg-red-100 text-xs font-medium border border-red-100">
                              <Square size={12} className="fill-current"/> Stop
                            </button>
                          ) : (
                            <button onClick={() => startTimer(t)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-50 text-emerald-800 hover:bg-emerald-100 text-xs font-medium border border-emerald-100">
                              <Play size={12} className="fill-current"/> Start
                            </button>
                          )}
                        </td>
                        <td className="px-5 py-4 text-right whitespace-nowrap">
                          <button onClick={() => setEditingTask(t)} className="p-1 text-stone-400 hover:text-emerald-800" title="Edit task" data-testid={`task-edit-btn-${t.id}`}><Pencil size={15}/></button>
                          <button onClick={() => removeTask(t)} className="p-1 text-stone-400 hover:text-red-600" title="Delete task" data-testid={`task-delete-btn-${t.id}`}><Trash2 size={15}/></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          )}
        </main>
      </div>

      <NewTaskDialog
        open={taskDialogOpen}
        onOpenChange={setTaskDialogOpen}
        clientsList={clientList}
        teamMembers={teamMembers}
        currentUserId={user?.id}
        onCreated={fetchAll}
      />
      <NewTaskDialog
        open={!!editingTask}
        onOpenChange={(o) => { if (!o) setEditingTask(null); }}
        task={editingTask}
        clientsList={clientList}
        teamMembers={teamMembers}
        currentUserId={user?.id}
        onCreated={fetchAll}
      />
    </div>
  );
}
